// Kept apart from the sorted imports below to preserve CSS cascade order.
import './video-page-styles.scss';

import * as Sentry from '@sentry/browser';
import 'Components/vm-toast-container/vm-toast-container';
import 'Components/vm-video-card/vm-video-card';
import 'Modules/ad-disclosure/components/vm-client-disclosure/vm-client-disclosure';
import { createBvaBrandAlerts, createVideoPageBrandAlerts } from 'Modules/video/brand-alerts';
import { webBrandAlertsConfig } from 'Modules/video/brand-alerts/config';
import { BrightcovePlayer } from 'Modules/video/brightcove-player';
import { trackCampaignVideoClicks } from 'Modules/video/campaign-tracking';
import 'Modules/video/components/vm-poll-overlay/vm-poll-overlay';
import 'Modules/video/components/vm-video-actions/vm-video-actions';
import 'Modules/video/components/vm-video-card-related/vm-video-card-related';
import 'Modules/video/components/vm-video-chapter/vm-video-chapter';
import 'Modules/video/components/vm-video-chapters/vm-video-chapters';
import 'Modules/video/components/vm-video-comments/vm-video-comments';
import 'Modules/video/components/vm-video-education-events/vm-video-education-events';
import 'Modules/video/components/vm-video-hub-card/vm-video-hub-card';
import 'Modules/video/components/vm-video-hub-related/vm-video-hub-related';
import 'Modules/video/components/vm-video-info-bottom-sheet/vm-video-info-bottom-sheet';
import 'Modules/video/components/vm-video-info/vm-video-info';
import 'Modules/video/components/vm-video-isi/vm-video-isi';
import 'Modules/video/components/vm-video-related-content/vm-video-related-content';
import { PollsController } from 'Modules/video/polls-controller';
import { setupChatbot } from 'Modules/video/setup-chatbot';
import { fetchRequest } from 'Utils/api-fetch-request';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

const learningJourneyShowMore = () => {
    const showMoreButtons = Array.from(document.querySelectorAll('.js_relatedGroupButton'));

    if (!showMoreButtons.length) return;

    showMoreButtons.forEach((button) => {
        if (button.dataset.initialized === 'true') return;

        button.dataset.initialized = 'true';
        button.addEventListener('click', () => {
            button.closest('.js_relatedContainerGroup').classList.add('is-expanded');
            button.remove();
        });
    });
};

const relatedContentPathSelector = async () => {
    const tabsWrap = document.querySelector('.js_relatedContainerTabs');

    if (!tabsWrap) return;

    const tabs = Array.from(tabsWrap.querySelectorAll('.js_relatedContainerTabsItem'));
    const activeContentElement = document.querySelector('.js_relatedContainerContent.is-active');
    const parentContainerElement = activeContentElement.parentElement;
    const contentElementClasses = activeContentElement.className;
    const autoplayButton = document.querySelector('.js_autoplayButton');

    const tabClickHandler = (tab, tabId) => {
        // Switch between different tabs and content elements
        const viewportWidth = window.innerWidth;
        document.querySelector('.js_relatedContainerContent.is-active').classList.remove('is-active');
        document.querySelector('.js_relatedContainerTabsItem.is-active').classList.remove('is-active');
        document.getElementById(`${tabId}-tab-content`).classList.add('is-active');
        tab.classList.add('is-active');

        const activeTooltipElement = document.querySelector('[data-toggle="tooltip"]:hover');
        if (activeTooltipElement) {
            if (viewportWidth >= 1024) {
                tab.addEventListener('mouseleave', () => {
                    $(activeTooltipElement).tooltip('hide');
                });
            } else {
                setTimeout(() => {
                    $(activeTooltipElement).tooltip('hide');
                }, 3000);
            }
        }
    };

    for (const [index, tab] of tabs.entries()) {
        const { tabUrl, tabId } = tab.dataset;
        const existingContentElement = document.querySelector(`.js_relatedContainerContent#${tabId}-tab-content`);

        if (existingContentElement) {
            // if the tab content elements already exists for this tab, just attach the event handler
            tab.addEventListener('click', () => {
                tabClickHandler(tab, tabId);
            });
        } else {
            // fetch content for each of the tabs and attach the event handler
            const newContentElement = document.createElement('div');
            newContentElement.className = contentElementClasses.replace('is-active', '').trim();
            newContentElement.id = `${tabId}-tab-content`;
            parentContainerElement.insertBefore(newContentElement, autoplayButton);

            try {
                const response = await fetch(tabUrl);
                if (!response.ok) {
                    throw new Error(`A response error occurred: ${response.statusText}`);
                }

                const data = await response.text();
                newContentElement.innerHTML = data || `<p>There are no videos to display</p>`;
            } catch {
                newContentElement.innerHTML = `<p>An error occurred while fetching videos. Please try again later.</p>`;
            } finally {
                tab.addEventListener('click', () => {
                    tabClickHandler(tab, tabId);
                });

                if (index === tabs.length - 1) {
                    // run the function only once after the last batch of data is collected
                    learningJourneyShowMore();
                }
            }
        }
    }
};

const relatedItemEventTracking = () => {
    const relatedContainers = document.querySelectorAll('.js_relatedContainer, .js_relatedContainerHighlighted');

    if (!relatedContainers.length) return;

    relatedContainers.forEach((relatedContainer) => {
        if (relatedContainer.dataset.initialized === 'true') {
            return;
        }
        relatedContainer.dataset.initialized = 'true';

        const {
            fsAbTestName: abTestName,
            fsAbTestVariant: abTestVariant,
            fsMaxLsIndex: maxLsIndex,
            channelId: channelId,
        } = relatedContainer.dataset;

        relatedContainer.addEventListener('click', (event) => {
            const promotedItem = event.target.closest('.js_promotedRelated');
            if (promotedItem && promotedItem.dataset) {
                window.vumedi?.mixpanel?.track('RelatedVideoClicked', {
                    video_id: context?.videoId || null,
                    video_slug: context?.videoSlug || null,
                    channel_id: promotedItem.dataset.relatedChannelId || channelId || context?.channelId || null,
                    clicked_video_id: promotedItem.dataset.relatedVideoId
                        ? parseInt(promotedItem.dataset.relatedVideoId, 10)
                        : null,
                    clicked_video_slug: promotedItem.dataset.relatedVideoSlug || null,
                    position: promotedItem.dataset.relatedPosition
                        ? parseInt(promotedItem.dataset.relatedPosition, 10)
                        : null,
                    promoted: true,
                    section: promotedItem.dataset.relatedSection || null,
                    template_version: context.templateVersion,
                });
                return;
            }

            const item = event.target.closest('.js_relatedVideoCard');

            if (item && item.dataset) {
                const {
                    fsRelatedItemIndex: itemIndex,
                    fsPathSelectorTabGroup: pathSelector,
                    fsItemInLectureSeries: isLsContent,
                    fsLearningJourneyGroup: learningJourneyGroup,
                    fsLearningJourneyItemIndex: learningJourneyItemIndex,
                } = item.dataset;
                const tags = item.querySelector('.js_relatedVideoCardTagsWrap')?.dataset.tags || [];

                if (relatedContainer.classList.contains('js_relatedContainer')) {
                    window.eventTracker.trackRelatedContentClick({
                        itemIndex,
                        tags,
                        pathSelector,
                        abTestName,
                        abTestVariant,
                        isLsContent,
                        maxLsIndex,
                        learningJourneyGroup,
                        learningJourneyItemIndex,
                    });
                }

                window.vumedi?.mixpanel?.track('RelatedVideoClicked', {
                    video_id: context?.videoId || null,
                    video_slug: context?.videoSlug || null,
                    channel_id: channelId || context?.channelId || null,
                    clicked_video_id: item.dataset.relatedVideoId ? parseInt(item.dataset.relatedVideoId, 10) : null,
                    clicked_video_slug: item.dataset.relatedVideoSlug || null,
                    position: itemIndex ? parseInt(itemIndex, 10) : null,
                    promoted: item.dataset.relatedPromoted === 'true',
                    section: item.dataset.fsRelatedContentGroup || null,
                    template_version: context.templateVersion,
                });
            }
        });
    });
};

const campaignItemEventTracking = () => {
    const campaignContainer = document.querySelector('.js_relatedCampaignsWrap');

    if (!campaignContainer) return;

    if (campaignContainer.dataset.initialized === 'true') {
        return;
    }
    campaignContainer.dataset.initialized = 'true';

    campaignContainer.addEventListener('click', (event) => {
        const campaignItem = event.target.closest('.js_promotedCampaign');
        if (!campaignItem) return;

        window.vumedi?.mixpanel?.track('RelatedVideoClicked', {
            video_id: context?.videoId || null,
            video_slug: context?.videoSlug || null,
            channel_id: campaignContainer.dataset.channelId || context?.channelId || null,
            clicked_video_id: campaignItem.dataset.relatedVideoId
                ? parseInt(campaignItem.dataset.relatedVideoId, 10)
                : null,
            clicked_video_slug: campaignItem.dataset.relatedVideoSlug || null,
            position: campaignItem.dataset.relatedPosition ? parseInt(campaignItem.dataset.relatedPosition, 10) : null,
            promoted: true,
            section: campaignItem.dataset.relatedSection || 'Campaign',
            template_version: context.templateVersion,
        });
    });
};

const jumpToRelatedContent = () => {
    const button = document.querySelector('.js_jumpToRelatedButton');
    const relatedContainer = document.querySelector('.js_relatedContainer');

    const buttonClickHandler = () => {
        relatedContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    if (!button || !relatedContainer) return;

    button.addEventListener('click', () => buttonClickHandler());
};

const collectHomefeedData = () => {
    const search = new URLSearchParams(window.location.href.split('/').pop());
    const idx = parseInt(search.get('idx'), 10);

    if (search.get('source') === 'homefeed' && Number.isInteger(idx)) {
        window.eventTracker.trackHomefeedRecommended(context.videoId, idx, search.has('campaign'));
    }
};

document.addEventListener('DOMContentLoaded', () => {
    window.vumedi.mixpanel?.track('VideoPlayerPageLoaded', {
        viewed_uuid: context.viewedUUID,
        video_id: context.videoId,
        video_slug: context.videoSlug,
        channel_id: context?.channelId || null,
        has_chapters: context.hasChapters,
        has_play_permission: context.playPermission,
        is_autoplay: context.autoplay,
        template_version: context.templateVersion,
    });

    if (context.playPermission) {
        // Brand-alert ↔ vm-video-comments orchestration. This block is the only
        // place that knows about both; neither component references the other.
        // The two talk exclusively through document-level events:
        //
        //   dispatched by vm-video-comments:
        //     vm-video-comments:sheet-visibility — mobile bottom sheet
        //       open/close transition with `{ open: boolean }` detail
        //
        // Listener is attached synchronously, BEFORE the async brightcove +
        // chatbot chain, so we can't miss an early emit. State is buffered in
        // a local var and applied to `brandAlerts` via `recompute()` once the
        // alert instance is created.
        //
        // Desktop: no blocking based on comments state — the sideguide slot
        // (right sidebar) and the comments organism (below the player) don't
        // overlap. The alert renders in its own column; the list stays where
        // it is.
        //
        // Mobile: the sideguide covers the mobile comments button, so the
        // user can't open the sheet while the sideguide is up. The other
        // direction (sheet up → alert can't fire) is handled here via
        // `setBlocked`: if the alert's trigger fires while the sheet is open,
        // `_showExpanded` bails and the impression is consumed.
        //
        // Listeners aren't removed — DOMContentLoaded fires once per page load
        // in this MPA. Add a guard here if this init ever runs twice.
        let brandAlerts = null;
        let sheetOpen = false;
        // Must match a BrandAlertEvent.Events choice — the tracking endpoint
        // validates against it and drops unknown values.
        const recompute = () => brandAlerts?.setBlocked(sheetOpen, 'comments_prevent_showing');

        // Mobile-only signal — the organism only fires this at the mobile
        // sheet open/close transition, so the listener never runs on desktop.
        document.addEventListener('vm-video-comments:sheet-visibility', (e) => {
            sheetOpen = e.detail.open;
            recompute();
        });

        const brightcovePlayer = new BrightcovePlayer();

        brightcovePlayer
            .initialize(
                document.getElementById(context.playerTagId),
                context.brightcoveId,
                context.accountId,
                context.playerId,
            )
            .then(async (player) => {
                const willShowProductTour = await setupChatbot(brightcovePlayer);

                if (context.viewedUUID) {
                    brandAlerts = createVideoPageBrandAlerts(webBrandAlertsConfig(), player);
                    // Video Assistant surface (BVA). Independent client, its own
                    // shell; it stays idle until the HCP interacts with the
                    // assistant, so it costs nothing on views that never do.
                    createBvaBrandAlerts(webBrandAlertsConfig(), player)?.start();
                    // Flush any sheet-open state accumulated while brandAlerts
                    // didn't exist yet.
                    recompute();
                    brandAlerts?.start(willShowProductTour ? 'chatbot_active_dismiss' : false);
                }

                const pollOverlay = document.querySelector('.js_pollOverlay');
                if (pollOverlay && pollOverlay.assignments?.length) {
                    for (const a of pollOverlay.assignments) {
                        window.vumedi.mixpanel?.track('PollLoaded', {
                            poll_id: a.poll_id,
                            assignment_id: a.id,
                            video_id: context.videoId,
                            video_slug: context.videoSlug,
                            channel_id: context?.channelId || null,
                        });
                    }
                    new PollsController(pollOverlay, player, pollOverlay.assignments);
                }
            });
    } else {
        document.querySelector('.js_loginRequiredPoster')?.addEventListener('click', () => {
            $('#loginOrRegisterModal').modal('show');
        });
    }

    // Autoplay
    const toggleAutoplay = document.getElementById(context.toggleAutoplayId);

    toggleAutoplay?.addEventListener('click', (e) => {
        const button = e.target;
        let url = context.autoplayURL;

        if (button.innerHTML === 'ON') {
            url = `${url}?autoplay=False`;
            button.innerHTML = 'OFF';
        } else {
            url = `${url}?autoplay=True`;
            button.innerHTML = 'ON';
        }

        fetchRequest(url)
            .then((data) => {
                window.autoplayEnabled = data.result;
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to toggle autoplay:', error);

                Sentry.captureException(error, {
                    tags: {
                        component: `#${context.toggleAutoplayId}`,
                    },
                    extra: {
                        url,
                        buttonState: button?.innerHTML,
                    },
                });
            });
    });

    $('.js_navigationLogInButton').on('click', (event) => {
        event.preventDefault();
        $('#loginOrRegisterModal').modal('show');
    });

    relatedContentPathSelector();
    relatedItemEventTracking();
    campaignItemEventTracking();
    trackCampaignVideoClicks();
    jumpToRelatedContent();
    learningJourneyShowMore();
    collectHomefeedData();

    document.addEventListener('visibilitychange', () => {
        const eventProps = {
            visibility_state: document.visibilityState,
            viewed_uuid: context.viewedUUID,
            video_id: context.videoId,
            video_slug: context.videoSlug,
            channel_id: context?.channelId || null,
            template_version: context.templateVersion,
        };

        if (document.visibilityState === 'hidden') {
            window.vumedi.mixpanel?.track('VideoPageVisibilityChanged', eventProps, { transport: 'sendBeacon' });
        } else {
            window.vumedi.mixpanel?.track('VideoPageVisibilityChanged', eventProps);
        }
    });
});
